/**
 * §30.F — 7-day mini bar-chart sparkline.
 * 80 × 20 px SVG, one bar per day, emerald-500 fill, zinc-200 axis line.
 * No labels, no tooltip. Pure visual encoding of relative magnitude.
 */

interface SparklineProps {
  /** 7 values, one per day, oldest-first. Fewer values are rendered left-padded. */
  values: number[];
  width?: number;
  height?: number;
}

export function Sparkline({ values, width = 80, height = 20 }: SparklineProps) {
  // Normalise to exactly 7 slots (pad left with 0 if fewer supplied).
  const slots: number[] = Array.from({ length: 7 }, (_, i) => {
    const offset = 7 - values.length;
    return i >= offset ? (values[i - offset] ?? 0) : 0;
  });

  const max = Math.max(...slots, 1);
  const barCount = 7;
  const gap = 2;
  const barW = Math.floor((width - gap * (barCount - 1)) / barCount);
  const axisY = height - 1;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden
      style={{ display: 'block', flexShrink: 0 }}
    >
      {/* Axis baseline */}
      <line
        x1={0}
        y1={axisY}
        x2={width}
        y2={axisY}
        stroke="#D4D4D8"
        strokeWidth={1}
      />
      {slots.map((v, i) => {
        const barH = Math.max(1, Math.round(((height - 3) * v) / max));
        const x = i * (barW + gap);
        const y = axisY - barH;
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={barW}
            height={barH}
            rx={1}
            fill="#10B981"
            fillOpacity={v === 0 ? 0.25 : 1}
          />
        );
      })}
    </svg>
  );
}
